import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaBell } from "react-icons/fa";
import { formatDateTimeNoSeconds } from "../utils/dateFormatter";
import {
    fetchHasUnread,
    fetchRecentAnnouncements,
    SUPPLIER_ANNOUNCEMENT_UNREAD_CHANGED,
} from "../utils/supplierAnnouncementApi";

export default function AnnouncementBell() {
    const navigate = useNavigate();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [hasUnread, setHasUnread] = useState(false);
    const [recentItems, setRecentItems] = useState([]);
    const [recentTotal, setRecentTotal] = useState(0);
    const [loadingRecent, setLoadingRecent] = useState(false);
    const containerRef = useRef(null);

    const loadUnread = useCallback(async () => {
        try {
            const unread = await fetchHasUnread();
            setHasUnread(unread);
        } catch {
            // セッション切れ時は authFetch がリダイレクトする
        }
    }, []);

    const loadRecent = useCallback(async () => {
        setLoadingRecent(true);
        try {
            const json = await fetchRecentAnnouncements();
            setRecentItems(json.data ?? []);
            setRecentTotal(json.total ?? 0);
        } catch {
            setRecentItems([]);
            setRecentTotal(0);
        } finally {
            setLoadingRecent(false);
        }
    }, []);

    // 初回・画面遷移・60秒ポーリング・フォーカス時に未読確認
    useEffect(() => {
        loadUnread();
        const intervalId = setInterval(loadUnread, 60000);
        const onFocus = () => loadUnread();
        const onUnreadChanged = () => loadUnread();
        window.addEventListener("focus", onFocus);
        window.addEventListener(SUPPLIER_ANNOUNCEMENT_UNREAD_CHANGED, onUnreadChanged);
        return () => {
            clearInterval(intervalId);
            window.removeEventListener("focus", onFocus);
            window.removeEventListener(SUPPLIER_ANNOUNCEMENT_UNREAD_CHANGED, onUnreadChanged);
        };
    }, [loadUnread, location.pathname]);

    const closeDropdown = useCallback(() => setOpen(false), []);

    useEffect(() => {
        if (!open) return undefined;
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                closeDropdown();
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [open, closeDropdown]);

    const handleItemClick = (id) => {
        closeDropdown();
        navigate(`/dashboard/announcements/${id}`, { state: { from: location.pathname } });
    };

    const handleViewAll = () => {
        closeDropdown();
        navigate("/dashboard/announcements");
    };

    const handleBellClick = () => {
        const willOpen = !open;
        setOpen(willOpen);
        if (willOpen) {
            loadUnread();
            loadRecent();
        }
    };

    const showViewAll = recentTotal > 5;

    return (
        <div className="relative" ref={containerRef}>
            <button
                type="button"
                onClick={handleBellClick}
                className="relative p-1 rounded-full hover:bg-gray-200 transition-colors"
                title="Announcements"
                aria-label="Announcements"
            >
                <FaBell className="w-5 h-5 text-gray-700" />
                {hasUnread && (
                    <span className="absolute top-0.5 right-0.5 w-2.5 h-2.5 bg-red-500 rounded-full border border-white" />
                )}
            </button>

            {open && (
                <div className="absolute top-full right-0 mt-1 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-[80] overflow-hidden">
                    <div className="px-3 py-2 border-b bg-gray-50 text-sm font-semibold text-gray-800">
                        Announcements
                    </div>
                    <ul className="max-h-80 overflow-y-auto">
                        {loadingRecent && (
                            <li className="px-3 py-4 text-sm text-gray-500 text-center">Loading...</li>
                        )}
                        {!loadingRecent && recentItems.length === 0 && (
                            <li className="px-3 py-4 text-sm text-gray-500 text-center">No announcements</li>
                        )}
                        {!loadingRecent &&
                            recentItems.map((item) => (
                                <li key={item.announcement_id}>
                                    <button
                                        type="button"
                                        onClick={() => handleItemClick(item.announcement_id)}
                                        className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 flex items-start gap-2"
                                    >
                                        {!item.is_read && (
                                            <span className="mt-1.5 w-2 h-2 bg-red-500 rounded-full flex-shrink-0" />
                                        )}
                                        <div className={`min-w-0 flex-1 ${item.is_read ? "pl-4" : ""}`}>
                                            <div
                                                className={`text-sm truncate ${
                                                    item.is_read ? "text-gray-700" : "font-semibold text-gray-900"
                                                }`}
                                            >
                                                {item.title}
                                            </div>
                                            <div className="text-xs text-gray-500 mt-0.5">
                                                {formatDateTimeNoSeconds(item.upd_date)}
                                            </div>
                                        </div>
                                    </button>
                                </li>
                            ))}
                        {!loadingRecent && showViewAll && (
                            <li>
                                <button
                                    type="button"
                                    onClick={handleViewAll}
                                    className="w-full text-center px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 font-medium"
                                >
                                    View all
                                </button>
                            </li>
                        )}
                    </ul>
                </div>
            )}
        </div>
    );
}
